import { Machine } from "../GameComponents/Machine";
import { ArrayUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Symbol } from "../GameComponents/Symbol";
import { ReelExtended } from "./ReelExtended";
import { PlayList } from "../Components/Sounds/PlayList";
import { AppContext } from "../../Common/AppContext";
import { ModuleManager } from "../Managers/ModuleManager";
import { DataManager } from "../Managers/DataManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { Settings } from "./Settings";
import { BaseView } from "../GameComponents/Base/BaseView";
import { FreespinsView } from "../GameComponents/Freespins/FreespinsView";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Symbols } from "../GameComponents/Symbols";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { Configuration, ReelConfigurationAttributes } from "../Components/Configuration";
import { Tweens } from "../Components/Effects/Tweens";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";
import { Communication } from "neo-games";
import { Reel } from "../GameComponents/Reel";
import { WinAnimatorExtended, WinAnimatorExtendedAttributes } from "./WinAnimatorExtended";

type IReel = Communication.Game.IReel;

@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    static RespinReelStopped: string = "onRespinReelStopped";
    static SpinReelRollFinished: string = "onSpinReelRollFinished";
    static RespinRollFinished: string = "onRespinRollFinished";
    static RespinTeaseStart: string = "onRespinTeaseStart";

    static Respin: boolean = false;

    protected _wasForced: boolean = false;

    protected noTrimContainer: ContainerElement;
    protected noTrimTeasingContainer: ContainerElement;
    protected teasingBlack: SwitchElement;

    protected _preRoll: number;
    protected _preRollSpeed: number;

    private _quickStopDelay: number = 10;

    protected reelsHorizontalIndexes: Array<number> = [];

    protected _reelsTopContainer: PIXI.Container;
    protected teasingSymbolCount: number = 0;
    protected teasingSymbolOnReel: Array<boolean> = [false, false, false, false, false, false, false];
    protected reelHasTetris: Array<boolean> = [false, false, false, false, false, false, false];
    protected delayTeasing: number = 60;
    protected delayTeasingTetris: number = 60;
    protected respinReelTeaseIndex: number = -1;

    protected startDelayArray: Array<number> = [0, 0, 0, 0, 0, 0, 0];

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    protected configurate(node: Element): void {
        super.configurate(node);

        this._preRoll = node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL))
            : 180;
        this._preRollSpeed = node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED))
            : 10;

        if (node.hasAttribute("StopDelay")) {
            this._quickStopDelay = this._stopDelay;
        }

        if (node.hasAttribute(MachineExtendedAttributes.QUICK_STOP_DELAY)) {
            this._quickStopDelay = parseInt(node.getAttribute(MachineExtendedAttributes.QUICK_STOP_DELAY));
        }

        if (node.hasAttribute(MachineExtendedAttributes.REELS_HORIZONTAL_INDEXES)) {
            this.reelsHorizontalIndexes = node
                .getAttribute(MachineExtendedAttributes.REELS_HORIZONTAL_INDEXES)
                .split(";")
                .map((value) => parseInt(value));
        }

        if (node.hasAttribute(MachineExtendedAttributes.TEASING_DELAY)) {
            this.delayTeasing = parseInt(node.getAttribute(MachineExtendedAttributes.TEASING_DELAY));
        }

        if (node.hasAttribute(MachineExtendedAttributes.TEASING_TETRIS_DELAY)) {
            this.delayTeasingTetris = parseInt(node.getAttribute(MachineExtendedAttributes.TEASING_TETRIS_DELAY));
        }

        if (node.hasAttribute(MachineExtendedAttributes.START_DELAY_ARRAY)) {
            this.startDelayArray = node
                .getAttribute(MachineExtendedAttributes.START_DELAY_ARRAY)
                .split(";")
                .map((value) => parseInt(value));
        }
    }

    /**
     * Initialize reference correct symbols and create reels.
     * @method initialize
     * @param symbols Symbols
     * @param reels Array<Array<number>>
     * @return void
     */
    public initialize(
        symbols: Symbols,
        reels: Array<Array<number>>,
        winAnimator: WinAnimator,
        teasing?: ContainerElement,
        teasingBlack?: SwitchElement,
        noTrimContainer?: ContainerElement,
        noTrimTeasingContainer?: ContainerElement,
        reelTopContainer?: ContainerElement,
    ): void {
        this._reelsTopContainer = reelTopContainer; // this before initialize
        this.noTrimContainer = noTrimContainer;
        this.noTrimTeasingContainer = noTrimTeasingContainer;
        this.teasingBlack = teasingBlack;

        super.initialize(symbols, reels, winAnimator, teasing);
    }

    /**
     * Create reels based on response and xml configuration.
     * @method createReels
     * @param reels Array<Array<number>> from initialization and based on response
     * @return void
     */
    protected createReels(reels: Array<Array<number>>): void {
        for (let i: number = 0; i < this._reelCount; i++) {
            this._reels.push(new ReelExtended());
        }

        for (let i: number = 0; i < this._reelCount; i++) {
            const reel = this._reels[i];

            if (this._reelConfiguration != null) {
                let configs: Array<string> = this._reelConfiguration.split(";");
                for (let j: number = 0; j < configs.length; j++) {
                    reel.configure(Configuration.Configurations[configs[j]]);
                }
            }
            reel.visible = false;
            reel.initialize(
                i > 0 ? this._reels[i - 1] : null,
                i < this._reelCount - 1 ? this._reels[i + 1] : null,
                i,
                this._symbols,
                reels[i],
                this._reelX[i],
                this._reelY[i],
                this._visibleSymbolCount[i],
                this._symbolTopCount,
                this._symbolBottomCount,
                this._symbolWidth,
                this._symbolHeight,
                this.startDelayArray[i] != null ? this.startDelayArray[i] : this._startDelay * i,
                this._stopDelay * i,
                this.winAnimator,
                this.reelsHorizontalIndexes.indexOf(i) != -1 ? true : false,
                this.noTrimContainer,
                this.noTrimTeasingContainer,
            );
            reel.on(Reel.ReelStopped, this.onReelStopped.bind(this));
            reel.on(ReelExtended.SpinReelRollFinished, this.onSpinReelRollFinished.bind(this));
            reel.on(ReelExtended.RespinReelStopped, this.onRespinReelStopped.bind(this));
            reel.on(ReelExtended.RespinPreRollReelFinished, this.respinPreRollReelFinished.bind(this));
            reel.on(ReelExtended.RespinRollReelFinished, this.respinRollReelFinished.bind(this));

            if (this.reelsHorizontalIndexes.indexOf(i) != -1 && this._reelsTopContainer) {
                this._reelsTopContainer.addChild(reel);
            } else {
                this._reelsContainer.addChild(reel);
            }
        }

        this._reelsContainer.visible = false;
        if (this._reelsTopContainer) {
            this._reelsTopContainer.visible = false;
        }
    }

    /**
     * Set symbols which stopped after reels will be stopped.
     * @method setStoppedSymbolsArray
     * @param value Array<number>
     * @return void
     */
    public setStoppedSymbolsArray(value: Array<IReel>): void {
        this._stoppedSymbolsArray = value;
    }

    /**
     * Start spinning reels.
     * @method spinStart
     * @param longRotation boolean
     * @return void
     */
    public spinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
    ): void {
        MachineExtended.Respin = false;
        this.teasingSymbolCount = 0;
        this.teasingSymbolOnReel = [false, false, false, false, false, false, false];

        if (DEBUG) {
            this.log("spinStart");
        }

        ReelExtended.Tease = false;

        if (this.maskSprite != null && this._reelsContainer != null) {
            this._reelsContainer.addChild(this.maskSprite);
            this._reelsContainer.mask = this.maskSprite;
        }

        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;
        this._wasForced = false;

        this.visible = true;
        this._reelsContainer.visible = true;
        if (this._reelsTopContainer) {
            this._reelsTopContainer.visible = true;
        }

        this.actualReelStopped = -1;
        this._stoppedReelIndexes = 0;

        Settings.RespinNumber = 0;

        this._startSpinTime = Date.now();

        if (this._reels != null) {
            for (let i: number = 0; i < this._reelCount; i++) {
                if (this._reels[i] != null) {
                    if (this._stoppedSymbolsArray.length > 0) {
                        this._reels[i].visible = true;
                        this._reels[i].setData(this._stoppedSymbolsArray[i]);
                        this._reels[i].currentSymbols = [];
                        this._reels[i].showSymbols(useTransform);
                        this._reels[i].spinStart();
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no stopped symbols");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reel with index: " + i);
                    }
                }
            }

            // if (longRotation != null && longRotation) {
            //     this._stopTimer.start(this._minSpinRotationLong);
            // } else {
            this._stopTimer.start(this._minSpinRotation);
            // }
        } else {
            if (DEBUG) {
                this.log("ERROR no reels");
            }
        }

        PlayList.play("SND_BaseRotationStart");
        PlayList.loop("SND_BaseRotation", 0, true);
    }

    /**
     * Stop reels rotation.
     * @method spinStop
     * @param event any - from timer
     * @param force boolean - default false used when spin_stop was dispatch
     * @param fromServer boolean - default false used when spin_stop was dispatch from server
     * @param legal
     * @return void
     */
    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
            this._wasForced = true;
        }

        if (DEBUG) {
            this.log("spinStop " + this._wasForced);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (
                !ModuleManager.freespinsActive() &&
                !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative") &&
                !MachineExtended.Respin
            ) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }
            if (this._wasForced && this._teasing != null && !MachineExtended.Respin) {
                // NOT reset tease when is respin and force
                for (let key in this._teasing.childs) {
                    if (this._teasing.childs[key] instanceof SwitchElement) {
                        this._teasing.childs[key].goToCase("EMPTY");
                    }
                }

                if (this.teasingBlack != null) {
                    this.teasingBlack.goToCase("EMPTY");
                }

                // teasing symbol stop shining
                if (<WinAnimatorExtended>this.winAnimator) {
                    (<WinAnimatorExtended>this.winAnimator).stopTeasingSymbolsShine();
                }

                PlayList.stop("SND_BaseTeaser", 5);

                ReelExtended.Tease = false;
            }

            // this._canTease5 = this._wasForced ? false : this.checkTeasing(5);
            this._canTease5 = this._wasForced ? false : MachineExtended.Respin ? false : this.checkTeasing(5);

            if (this._reels != null) {
                let teasingDelay: number = 0;
                let teasingCounter: number = 0;
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);

                            let stopDelay = this._stopDelay * i;
                            let useForceCustomDelay = false;

                            if (i > 1 && i < this._reels.length - 1) {
                                // --------- teasing HIT ----------
                                this._reels[i].canTeasingSymbolHit =
                                    this._reels[i - 1].canTeasingSymbolHit == true || i == 2
                                        ? this._stoppedSymbolsArray[i].symbols.indexOf(Settings.SymbolTeasing) !== -1
                                        : false;

                                if (
                                    MachineExtended.Respin &&
                                    this.canReelTeaseRespin(i) &&
                                    this.teasingSymbolOnReel[i] === false &&
                                    this.reelHasTetris[i]
                                ) {
                                    teasingDelay += this.delayTeasingTetris;

                                    if (this.respinReelTeaseIndex === -1) {
                                        ReelExtended.CanTeaseRespin = true; // set if CAN RESPIN TEASE

                                        this.respinReelTeaseIndex = i;
                                    }
                                }
                            }

                            if (teasingDelay > 0) {
                                teasingCounter++;
                            }

                            if (this._wasForced || !this._canTease5) {
                                this._reels[i].spinStop(
                                    this._wasForced,
                                    this._wasForced ? this._quickStopDelay * i : stopDelay,
                                    this._wasForced ? true : useForceCustomDelay,
                                    teasingDelay,
                                    teasingCounter,
                                );
                            } else {
                                if (i <= 4) {
                                    this._reels[i].spinStop(
                                        this._wasForced,
                                        this._wasForced ? this._quickStopDelay * i : stopDelay,
                                        this._wasForced ? true : useForceCustomDelay,
                                    );
                                }
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    // protected getLegalRemainingSpinDuration(): number {
    //     return 0;       // handled by ActionSet's action lock
    // }

    protected checkTeasing(reelIndex: number): boolean {
        let count = 0;

        if (reelIndex > this._reelCount - 1) {
            return false;
        }

        for (let i = 2; i < reelIndex; i++) {
            for (let j = 0; j <= this._stoppedSymbolsArray[i].symbols.length; j++) {
                if (this._stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                    count++;
                }
            }
        }

        return count >= 3;
    }

    public respinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbolsCases?: Array<Array<string>>,
    ): void {
        MachineExtended.Respin = false;
        this.reelHasTetris = [false, false, false, false, false, false, false];
        this.respinReelTeaseIndex = -1;

        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;
        this._wasForced = false;

        this.visible = true;
        this._reelsContainer.visible = true;
        if (this._reelsTopContainer) {
            this._reelsTopContainer.visible = true;
        }

        this.actualReelStopped = -1;
        this._stoppedReelIndexes = 0;

        if (Settings.RespinNumber < 3) {
            Settings.RespinNumber++;
        } else {
            Settings.RespinNumber = 1;
        }

        this._startSpinTime = Date.now();

        if (this._reels != null) {
            let delayReelMultiplier: number = -1;
            for (let i = 0; i < this._reelCount; i++) {
                if (this._reels[i] != null) {
                    if (this._stoppedSymbolsArray.length > 0) {
                        this._reels[i].visible = true;
                        this._reels[i].setData(this._stoppedSymbolsArray[i]);
                        this._reels[i].currentSymbols = [];

                        this._reels[i].showRespinSymbols(useTransform, currentSymbolsCases[i]);

                        if (this._reels[i].hasEmptySymbols()) {
                            this.reelHasTetris[i] = true;
                            this._reels[i].respinStart(++delayReelMultiplier);
                        } else {
                            this._reels[i].skipSpin();
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no stopped symbols");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reel with index: " + i);
                    }
                }
            }

            // if (longRotation != null && longRotation) {
            //     this._stopTimer.start(this._minSpinRotationLong);
            // } else {
            this._stopTimer.start(this._minSpinRotation);
            // }
        } else {
            if (DEBUG) {
                this.log("ERROR no reels");
            }
        }
    }

    private canReelTeaseRespin(reelIndex: number): boolean {
        let count = 0;
        for (let i = 2; i < reelIndex; i++) {
            if (this.teasingSymbolOnReel[i] === false) {
                for (let j = 0; j < this._stoppedSymbolsArray[i].symbols.length; j++) {
                    if (this._stoppedSymbolsArray[i].symbols[j] == Settings.SymbolTeasing) {
                        count++;
                    }
                }
            }
        }

        return count + this.teasingSymbolCount >= 3;
    }

    // respinPreRollReelFinished is emit when new symbols fall down without impact animation (delay) = only symbols moving duration
    public respinPreRollReelFinished(reelIndex: number): void {
        let count: number = 0;
        let skipRespinTeasing: boolean = false;

        for (let i: number = 0; i < this._reelCount; i++) {
            if (this._reels[i].respinPreRollFinished) {
                count++;

                if (
                    this._reels[i].allSymbolsEmptyBeforeRespin &&
                    this.respinReelTeaseIndex != -1 &&
                    i < this.respinReelTeaseIndex
                ) {
                    skipRespinTeasing = true; // skip teasing on reel with all symbols in EMPTY case, because wait for new symbols and then start teasing (in respinRollReelFinished)
                }
            }
        }

        if (count > 0) {
            if (this.respinReelTeaseIndex != -1 && ReelExtended.CanTeaseRespin == true) {
                // if(count == (this._reelCount - 1)) { // tease only one reel on end (reels count - 1)
                if (count == this._reelCount - 1 && this.teasingSymbolCount == 3 && !skipRespinTeasing) {
                    // start teasing only all pre roll symbols DONE (symbols on screen - not new symbols) and on screen are 3 teasing symbols or skip
                    // PRE ROLL TEASING
                    this.respinTease(this.respinReelTeaseIndex);
                }
            }
        }
    }

    // respinRollReelFinished is emit without impact delay
    public respinRollReelFinished(reelIndex: number): void {
        if (
            this.teasingSymbolOnReel[reelIndex] == false &&
            this._stoppedSymbolsArray[reelIndex].symbols.indexOf(Settings.SymbolTeasing) != -1
        ) {
            this.teasingSymbolCount++;
            this.teasingSymbolOnReel[reelIndex] = true;
        }

        let count: number = 0;

        for (let i: number = 0; i < this._reelCount; i++) {
            if (this._reels[i].respinRollFinished) {
                count++;
            }
        }

        if (count > 0) {
            if (count == this._reelCount) {
                this.emit(MachineExtended.RespinRollFinished);
            }

            if (this.respinReelTeaseIndex != -1 && ReelExtended.CanTeaseRespin == true) {
                if (count == this._reelCount) {
                    // THIS IS PRIORITY !!! stop tease if all reels finished
                    if (reelIndex == this.respinReelTeaseIndex) {
                        if (this.teasingSymbolCount == 4) {
                            PlayList.play("SND_BaseScatter04");
                        } else {
                            PlayList.play("SND_BaseTeaserFail");
                        }
                    }

                    this.respinTeaseStop(this.respinReelTeaseIndex);
                } else if (
                    count == this.respinReelTeaseIndex ||
                    count == this._reelCount - 1 ||
                    this.teasingSymbolCount == 3
                ) {
                    // POST ROLL TEASING
                    if (
                        this.reelHasTetris[reelIndex] &&
                        reelIndex != this.respinReelTeaseIndex &&
                        this.teasingSymbolCount == 3
                    ) {
                        PlayList.play("SND_BaseScatter03", 0, false);
                    }
                    this.respinTease(this.respinReelTeaseIndex);
                }
            }
        }
    }

    /**
     * Dispatched onMachineStopped when all reels are stopped.
     * @method onReelStopped
     * @param reelIndex number - reel index
     * @return void
     */
    public onReelStopped(reelIndex: number): void {
        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;
        if (this.reelsHorizontalIndexes.indexOf(reelIndex) != -1) {
            this._reels[reelIndex].visible = false;
        }

        this.emit(Machine.ReelStopped);

        if (this._stoppedReelIndexes === this._reelCount) {
            PlayList.stop("SND_BaseRotation", 10);

            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stopTimer.stop();

            ReelExtended.Tease = false;

            if (DEBUG) {
                const old = AppContext.gameConfiguration.MinGameRoundDuration;
                const current = AppContext.gameConfiguration.GameRoundMinSpan;
                const minDuration = current > 0 ? current : old > 0 ? old : 0;
                const spinDuration = Date.now() - this._startSpinTime;

                if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                    this.log("Spin time duration: " + spinDuration + " ms");
                } else {
                    console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
                }
            }

            this._reelsContainer.visible = false;
            if (this._reelsTopContainer) {
                this._reelsTopContainer.visible = false;
            }

            this.visible = false;
            this.emit(Machine.MachineStopped, false);
        }
    }

    public onRespinReelStopped(reelIndex: number): void {
        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;

        this.emit(MachineExtended.RespinReelStopped);

        if (this._stoppedReelIndexes === this._reelCount) {
            PlayList.stop("SND_BaseRotation", 10);

            // remove symbols from reels
            for (let i: number = 0; i < this._reelCount; i++) {
                this._reels[i].returnAllSymbols();
            }

            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }

            ReelExtended.Tease = false;

            if (DEBUG) {
                const old = AppContext.gameConfiguration.MinGameRoundDuration;
                const current = AppContext.gameConfiguration.GameRoundMinSpan;
                const minDuration = current > 0 ? current : old > 0 ? old : 0;
                const spinDuration = Date.now() - this._startSpinTime;

                if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                    this.log("Spin time duration: " + spinDuration + " ms");
                } else {
                    console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
                }
            }

            this._stopTimer.stop();

            this._reelsContainer.visible = false;
            if (this._reelsTopContainer) {
                this._reelsTopContainer.visible = false;
            }

            this.visible = false;
            this.emit(Machine.MachineStopped, true);
        }
    }

    protected respinTease(reelIndex: number): void {
        if (reelIndex) {
            if (
                this._teasing != null &&
                this._teasing.childs["Teasing" + reelIndex] &&
                !this._reels[reelIndex].isRespinTease
            ) {
                ReelExtended.Tease = true;
                this._reels[reelIndex].isRespinTease = true;
                this._reels[reelIndex].respinTease(); // after isRespinTease = true

                PlayList.play("SND_BaseTeaser");

                (<SwitchElement>this._teasing.childs["Teasing" + reelIndex]).goToCase("START");

                if (this.teasingBlack != null) {
                    this.teasingBlack.goToCase("TEASING_" + reelIndex);
                }

                // teasing symbol start shining with delay
                if (<WinAnimatorExtended>this.winAnimator) {
                    (<WinAnimatorExtended>this.winAnimator).runTeasingSymbolsShine(25);
                }

                this.emit(MachineExtended.RespinTeaseStart);
            }
        }
    }

    protected respinTeaseStop(reelIndex: number): void {
        if (this._teasing != null && reelIndex && this._reels[reelIndex].isRespinTease) {
            PlayList.stop("SND_BaseTeaser", 5);

            let teaseChild = this._teasing.childs["Teasing" + reelIndex];

            if (teaseChild) {
                if (
                    (<SwitchElement>teaseChild).getCurrentCase() == "START" ||
                    (<SwitchElement>teaseChild).getCurrentCase() == "LOOP"
                ) {
                    ReelExtended.Tease = false;
                    this._reels[reelIndex].respinTeaseStop(); // before isRespinTease = false
                    this._reels[reelIndex].isRespinTease = false;

                    (<SwitchElement>teaseChild).goToCase("STOPPING");

                    if (this.teasingBlack != null) {
                        if (!ModuleManager.pickEnabled()) {
                            this.teasingBlack.goToCase("RESET");
                        } else {
                            this.teasingBlack.goToCase("BLACK_ALL");
                        }
                    }

                    // teasing symbol stop shining
                    if (<WinAnimatorExtended>this.winAnimator) {
                        (<WinAnimatorExtended>this.winAnimator).stopTeasingSymbolsShine();
                    }
                }
            }
        }
    }

    protected tease(reelIndex: number): void {
        switch (reelIndex) {
            case 4:
                if (this._canTease5) {
                    ReelExtended.Tease = true;
                    this._reels[5].spinTease([Settings.SymbolTeasing], this.delayTeasing);
                    this._reels[6].spinTease([], this.delayTeasing + this._stopDelay);

                    PlayList.play("SND_BaseTeaser");

                    if (this._teasing != null) {
                        (<SwitchElement>this._teasing.childs["Teasing5"]).goToCase("START");
                    }

                    if (this.teasingBlack != null) {
                        this.teasingBlack.goToCase("TEASING_" + 5);
                    }
                }

                break;
            case 5:
                if (this._canTease5) {
                    ReelExtended.Tease = false;

                    if (this._teasing != null) {
                        if ((<SwitchElement>this._teasing.childs["Teasing5"]).getCurrentCase() == "LOOP") {
                            (<SwitchElement>this._teasing.childs["Teasing5"]).goToCase("STOPPING");

                            // teasing symbol stop shining
                            if (<WinAnimatorExtended>this.winAnimator) {
                                (<WinAnimatorExtended>this.winAnimator).stopTeasingSymbolsShine();
                            }
                        }
                    }

                    if (this.teasingBlack != null && !ModuleManager.pickEnabled()) {
                        this.teasingBlack.goToCase("RESET");
                    }
                }

                break;
            case 6:
                if (this._canTease5) {
                    PlayList.stop("SND_BaseTeaser", 5);

                    let count = 0;

                    for (let i = 0; i <= reelIndex; i++) {
                        for (let j = 0; j <= this._stoppedSymbolsArray[i].symbols.length; j++) {
                            if (this._stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                                count++;
                            }
                        }
                    }
                }

                break;
        }
    }

    public onSpinReelRollFinished(reelIndex: number): void {
        if (this._stoppedSymbolsArray[reelIndex].symbols.indexOf(Settings.SymbolTeasing) != -1) {
            this.teasingSymbolCount++;
            this.teasingSymbolOnReel[reelIndex] = true;
        }

        this.tease(reelIndex);

        this.emit(MachineExtended.SpinReelRollFinished, reelIndex);
    }
}

export const MachineExtendedAttributes = {
    QUICK_STOP_DELAY: "QuickStopDelay",
    REELS_HORIZONTAL_INDEXES: "ReelsHorizontalIndexes",
    TEASING_DELAY: "TeasingDelay",
    TEASING_TETRIS_DELAY: "TeasingTetrisDelay",
    START_DELAY_ARRAY: "StartDelayArray",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/